import {
  isReservedScheduleType,
  isSelectableReservedScheduleType,
  ReservedScheduleTypeEnum,
} from "@/lib/enums/reserved-schedule-type";
import { logError } from "@/lib/utils/log-error";

/**
 * O que o resolver precisa do contexto da rota. Tipo estrutural mínimo de propósito: o contexto real
 * da rota satisfaz isto sem cast, e o teste não precisa montar um contexto inteiro.
 */
export interface ReservedScheduleModalityRouteContext {
  /** Transitório — some junto com a flag. Ver o comentário da união abaixo. */
  ldClient: {
    getReservedScheduleTypeByUser: () => number;
  };
  user: {
    /** Transitório — some junto com a flag. */
    codParceiroPortoSeguro: boolean;
    modalidadesDeParceria: number[];
  };
}

export interface ReservedScheduleModality {
  /**
   * Modalidades a que este membro tem acesso, em ordem determinística (valor crescente) e sem
   * repetição. **Nunca vazia** — a convencional está sempre nela.
   *
   * Uma opção só significa que não há escolha a fazer, e é o caso da maioria: o campo de categoria
   * não é renderizado e a tela fica idêntica à de hoje.
   */
  accessibleModalities: ReservedScheduleTypeEnum[];
  /**
   * Pré-seleção, sempre uma das `accessibleModalities`. Preserva o comportamento atual e o direito
   * a 1h/quinzenal de quem já tinha, com uma exceção deliberada: a Psico, que é descontinuada e não
   * ofertável na criação, cai para a convencional.
   */
  defaultModality: ReservedScheduleTypeEnum;
}

/**
 * A flag `modalidade-periodo-garantido-por-usuario` devolve um `number` cru — nada garante que o
 * valor servido esteja entre as sete modalidades. Diante de um valor fora do enum, o backend
 * **lança** (`UsuarioTermoDeUsoNegocio`), e o app não pode ser mais permissivo que ele em silêncio:
 * um termo só "plausível" faz o gate de `/create` consultar um documento que não é o daquele PG.
 *
 * Então cai para a convencional, e o valor inesperado vai para o Sentry — que é o erro visível que
 * um fallback silencioso não dá.
 */
const toKnownModality = (requestedModality: number): ReservedScheduleTypeEnum => {
  if (isReservedScheduleType(requestedModality)) {
    return requestedModality;
  }

  logError(
    new Error(
      `Modalidade de Período Garantido fora de CodModalidadePeriodoGarantido: ${requestedModality}`,
    ),
    { requestedModality },
  );

  return ReservedScheduleTypeEnum.Livance;
};

/**
 * Resolve, em **um lugar só**, quais modalidades de Período Garantido valem para este membro.
 *
 * ## A união é transitória, e é ela que torna o corte da flag seguro
 *
 * O acesso vem de **duas** fontes ao mesmo tempo:
 *
 * 1. o **banco** — a coluna `codModalidadePeriodoGarantido` de `tbParceirosIntegracoes` diz que
 *    categoria cada parceiro concede, e o `UserContext` traz as dos parceiros com integração ativa;
 * 2. a **flag** `modalidade-periodo-garantido-por-usuario` e o `codParceiroPortoSeguro`, que são
 *    como o acesso funciona hoje.
 *
 * Somar em vez de substituir existe para uma razão específica: quem foi liberado **direto no painel
 * do LaunchDarkly**, fora do `AssociarMembroParceriaCommand`, não tem linha em
 * `tbUsuariosIntegracoes` — e trocar a fonte de uma vez tiraria a modalidade dessas pessoas em
 * silêncio, com o sintoma chegando por suporte. Com a união, ninguém perde acesso, e a auditoria do
 * targeting deixa de ser pré-requisito para publicar.
 *
 * A flag tem default `1` (Livance), que já está na união — então, para quem não tem targeting
 * nenhum, a fonte 2 não contribui com nada.
 *
 * **Remoção:** as duas entradas transitórias saem no PR 3 da KRA-518, depois da auditoria. Ali a
 * lista passa a vir só do banco, e `defaultModality` passa a ser a modalidade de parceria de menor
 * valor em vez da fórmula preservada abaixo.
 *
 * **Síncrono**: nenhum I/O. `getReservedScheduleTypeByUser()` lê a flag já carregada em memória,
 * então não há assincronia a esconder aqui — e sem `await` na assinatura, ninguém consegue enfiar
 * uma query dentro depois. O único efeito colateral é o `logError` do caminho impossível.
 *
 * **O termo de uso não sai daqui.** Era função de `defaultModality`; com a escolha explícita da
 * categoria (T16) passa a ser função da **escolha**, que só existe depois do submit. Quem precisa
 * dele indexa `TERMS_OF_USE_BY_MODALITY` sobre a modalidade que está de fato usando.
 */
export const resolveReservedScheduleModality = (
  context: ReservedScheduleModalityRouteContext,
): ReservedScheduleModality => {
  const conventionalModality = ReservedScheduleTypeEnum.Livance;

  const modalityByFlag = context.ldClient.getReservedScheduleTypeByUser();
  const legacyPartnershipModality = context.user.codParceiroPortoSeguro
    ? ReservedScheduleTypeEnum.Porto
    : null;

  const accessibleModalities: ReservedScheduleTypeEnum[] = [
    conventionalModality,
    ...context.user.modalidadesDeParceria,
    modalityByFlag,
    ...(legacyPartnershipModality === null ? [] : [legacyPartnershipModality]),
  ]
    // A Psico está descontinuada e não pode ser ofertada nem que um parceiro passe a concedê-la,
    // nem que a flag aponte para ela. O guard também recusa valor fora do enum, então lixo servido
    // pela flag não vira opção renderizável.
    .filter(isSelectableReservedScheduleType)
    .filter((modality, index, modalities) => modalities.indexOf(modality) === index)
    .sort((left, right) => left - right);

  // Fórmula preservada: a parceria vence o valor da flag, que é o que garante a 1h e a quinzenal a
  // quem já tinha. A LISTA cresce com o banco, mas ela ainda não tem consumidor — então a única
  // mudança de comportamento desta entrega é a exceção da Psico, logo abaixo. Alargar a
  // pré-seleção mudaria duração e frequência do formulário de quem hoje cria PG convencional, fora
  // de qualquer flag.
  const preferredModality = toKnownModality(legacyPartnershipModality ?? modalityByFlag);

  return {
    accessibleModalities,
    // A pré-seleção sai de dentro das acessíveis, e isso **muda** o comportamento em um caso: hoje,
    // `flag = Psico` pré-seleciona a Psico e faz criar um PG Psico novo. A Psico está descontinuada
    // e não é ofertável na criação, então esse caminho não deveria existir — quem estiver nesse
    // targeting passa a pré-selecionar a convencional, com o termo convencional. Os PGs Psico já
    // criados não passam por aqui: este resolver só alimenta o fluxo de criação, e a leitura deles
    // usa o valor gravado no próprio PG.
    //
    // A queda não vai para o Sentry de propósito: é regra de negócio, não defeito de configuração —
    // ao contrário do valor fora do enum, que `toKnownModality` registra.
    defaultModality: accessibleModalities.includes(preferredModality)
      ? preferredModality
      : conventionalModality,
  };
};
